/**
 * Home hero workflow card: a looping, illustrative smart-routing run. Static under reduced motion;
 * pauses while the card is off-screen or the tab is hidden.
 */
import { HERO_STEPS, type HeroRoute } from '../../components/hero-panel';
import { icon } from '../../components/icons';
import { fillDemoData } from '../../content/demo-data';
import { $, $$, prefersReducedMotion, requireElement } from '../../lib/dom';
import { onPageCleanup, trackObserver } from '../router/page-lifecycle';

type StepState = 'done' | 'run' | 'human' | 'queued';

const CHIPS: Readonly<Record<StepState, string>> = {
  done: 'Complete',
  run: 'In Progress',
  human: 'Human Checkpoint',
  queued: 'Queued',
};

/** Timings (ms) of the loop. */
const TIMING = {
  resume: 1600,
  beforeNextStep: 450,
  stepRun: 1500,
  humanReview: 2600,
  afterApproval: 700,
  restart: 900,
  endPause: 2800,
  hiddenRetry: 400,
} as const;

/** The card is rendered mid-run with step 4 (evidence package) in progress. */
const RENDERED_STEP = 3;
const START_CLOCK_SECONDS = 14 * 3600 + 14 * 60;

const pad = (n: number): string => String(n).padStart(2, '0');

export function initHeroPanel(root: HTMLElement): void {
  const panel = $('#hero-panel', root);
  if (!panel || prefersReducedMotion()) return;

  const steps = $$('.step', panel);
  const fill = requireElement('.hp-steps', panel);
  const routes = $$('[data-route]', panel);
  const logEl = requireElement('[data-log]', panel);
  const timeEl = requireElement('[data-time]', panel);
  const stageEl = requireElement('[data-stage]', panel);
  const reviewFloat = $('[data-review-float]', root);
  const reviewText = $('[data-review-text]', root);

  let timer: number | undefined;
  let visible = true;
  let clockSeconds = START_CLOCK_SECONDS;
  onPageCleanup(() => {
    clearTimeout(timer);
  });
  trackObserver(
    new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        visible = entry.isIntersecting;
      });
    }),
  ).observe(panel);

  /** Runs `fn` after `ms`, holding while the card is hidden. */
  const wait = (ms: number, fn: () => void): void => {
    timer = window.setTimeout(function tick() {
      if (!document.hidden && visible) fn();
      else timer = window.setTimeout(tick, TIMING.hiddenRetry);
    }, ms);
  };
  const setStep = (li: HTMLElement, state: StepState, i: number): void => {
    li.className = `step ${state}`;
    requireElement('.node', li).innerHTML = icon(state === 'done' ? 'check' : HERO_STEPS[i].icon);
    const chip = requireElement('.chip', li);
    chip.className = `chip ${state}`;
    chip.textContent = CHIPS[state];
  };
  const setFill = (i: number): void => {
    fill.style.setProperty('--p', String(Math.max(0, Math.min(1, i / (steps.length - 1)))));
  };
  const log = (i: number): void => {
    clockSeconds += 23 + Math.floor(Math.random() * 40);
    timeEl.textContent = `${pad(Math.floor(clockSeconds / 3600) % 24)}:${pad(Math.floor(clockSeconds / 60) % 60)}:${pad(clockSeconds % 60)}`;
    logEl.classList.remove('tick');
    logEl.getBoundingClientRect(); // forces a reflow so the CSS animation restarts
    logEl.classList.add('tick');
    logEl.textContent = fillDemoData(HERO_STEPS[i].log);
  };
  const highlightRoute = (route: HeroRoute | ''): void => {
    routes.forEach((r) => r.classList.toggle('on', r.dataset.route === route));
  };
  const reset = (): void => {
    steps.forEach((li, i) => {
      setStep(li, 'queued', i);
    });
    setFill(0);
    highlightRoute('');
    stageEl.textContent = 'Notice received';
    reviewFloat?.classList.remove('lit');
    wait(TIMING.restart, () => {
      run(0);
    });
  };
  const run = (i: number): void => {
    if (i >= steps.length) {
      stageEl.textContent = 'Case updated';
      highlightRoute('');
      wait(TIMING.endPause, reset);
      return;
    }
    const step = HERO_STEPS[i];
    highlightRoute(step.route);
    setFill(i);
    stageEl.textContent = step.t;
    if (step.human) {
      setStep(steps[i], 'human', i);
      reviewFloat?.classList.add('lit');
      if (reviewText) reviewText.textContent = 'Waiting on Morgan Lee · approve, modify or reject';
      wait(TIMING.humanReview, () => {
        if (reviewText) reviewText.textContent = 'Approved by Morgan Lee';
        log(i);
        setStep(steps[i], 'done', i);
        wait(TIMING.afterApproval, () => {
          run(i + 1);
        });
      });
    } else {
      setStep(steps[i], 'run', i);
      wait(TIMING.stepRun, () => {
        setStep(steps[i], 'done', i);
        log(i);
        wait(TIMING.beforeNextStep, () => {
          run(i + 1);
        });
      });
    }
  };

  setFill(RENDERED_STEP);
  wait(TIMING.resume, () => {
    setStep(steps[RENDERED_STEP], 'done', RENDERED_STEP);
    log(RENDERED_STEP);
    wait(TIMING.beforeNextStep, () => {
      run(RENDERED_STEP + 1);
    });
  });
}
